/* eslint-disable react/jsx-no-useless-fragment */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import * as R from 'ramda';
import Container from '../common/components/Container/Container';
import Button, { ButtonStyle } from '../common/components/Button/Button';
import ButtonContainer from '../common/components/Button/ButtonContainer';
import TitleContainer from '../common/components/TitleContainer/TitleContainer';
import withRouteAnalytics from '../services/analytics/withRouteAnalytics';
import { Screens } from '../services/analytics/screens';
import { useCredentialStore } from '../providers/CredentialStoreContext';
import { useAuthentication } from '../providers/AuthenticationContext';
import { useValidation } from '../providers/ValidationContext';
import { Credentials, StoreStatus } from '../common/types';
import Result from '../common/result';
import { CustomError, TechnicalError } from '../common/errors';
import SVGCivicMe from '../common/assets/svg/icons/SVGCivicMe';
import { useFlowConfig } from '../providers/FlowConfigContext';
import { Routes } from '../config/routes';
import { useGKNService } from '../providers/GatekeeperNetworkService/GatekeeperNetworkServiceContext';
import Loading from '../common/components/Loading/Loading';
import { useScopeRequest } from '../providers/ScopeRequestContext';

function StoreCredentialsScreen(): JSX.Element | null {
  const { flowSuccessful } = useFlowConfig();
  const { prePassCredentialStorageEnabled } = useGKNService();
  const { scopeRequest } = useScopeRequest();
  const { getRouteForCurrentStatus, getCurrentCredentials, hasNewCredentialsToStore, validationId } = useValidation();
  const {
    storeStatus,
    storeCredentials,
    retrieveExistingInProgress,
    setStoreStatus,
    credentialClientInst,
    existingStoredCredentials,
  } = useCredentialStore();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [storedCredentials, setStoredCredentials] = useState<Credentials | null>(null);
  const { user } = useAuthentication();
  const currentLocation = useLocation();
  const [failureCount, setFailureCount] = useState<number>(0);

  const handleCredentialsStore = useCallback(
    async (abortController?: AbortController): Promise<Result<CustomError, boolean | undefined>> => {
      if (!credentialClientInst) {
        return Result.failed(new TechnicalError('Unexpected error: no credential storage client'));
      }
      setStoreStatus(StoreStatus.confirmed);
      const currentCredentialsResult = await getCurrentCredentials();

      if (!currentCredentialsResult.isSuccessful()) {
        return Result.failed(currentCredentialsResult.failure());
      }

      const currentCredentials = (await getCurrentCredentials()).success();
      if (abortController?.signal.aborted) {
        return Result.failed(new TechnicalError('Aborted'));
      }
      // if there are any chosenCredentialIds that are not in the existing stored credential Ids, return false so that
      // the new ones can be stored
      const newCredentialsToStore = currentCredentials.credentials.filter(
        (cred) => !existingStoredCredentials?.map(R.prop('credentialId')).includes(cred.id),
      );

      if (storedCredentials === currentCredentials || newCredentialsToStore.length === 0) {
        setStoreStatus(StoreStatus.complete);
        return Result.noValueSuccess();
      }
      const storeCredentialsResult = await storeCredentials(
        { credentials: newCredentialsToStore, evidences: currentCredentials.evidences },
        abortController,
      );
      if (!storeCredentialsResult.isSuccessful()) {
        return Result.failed(storeCredentialsResult.failure());
      }
      if (abortController?.signal.aborted) {
        return Result.failed(new TechnicalError('Aborted'));
      }
      setStoredCredentials(currentCredentials);
      return Result.noValueSuccess();
    },
    [
      credentialClientInst,
      existingStoredCredentials,
      getCurrentCredentials,
      setStoreStatus,
      storeCredentials,
      storedCredentials,
    ],
  );

  useEffect(() => {
    if (!user?.did || storeStatus === StoreStatus.failed) {
      setStoredCredentials(null);
    }
  }, [user?.did, storeStatus]);

  const onConfirm = useCallback(() => {
    hasNewCredentialsToStore(validationId).then((newCredentialsToStore: boolean) => {
      if (!storedCredentials && newCredentialsToStore) {
        handleCredentialsStore().then((result) => {
          if (!result.isSuccessful()) {
            setStoreStatus(StoreStatus.failed);
            // we track the failed counts so that we can show the skip button after 2 failures
            setFailureCount(failureCount + 1);
          } else {
            setStoreStatus(StoreStatus.complete);
          }
        });
        return;
      }
      flowSuccessful();
    });
  }, [
    hasNewCredentialsToStore,
    validationId,
    storedCredentials,
    flowSuccessful,
    handleCredentialsStore,
    setStoreStatus,
    failureCount,
  ]);

  const skipStorageConfirm = useCallback(() => {
    navigate(currentLocation?.state?.prevPath, { state: { prevPath: Routes.STORE_CREDENTIALS } });
  }, [currentLocation?.state?.prevPath, navigate]);

  useEffect(() => {
    const abortController = new AbortController();

    // don't navigate back to validation unless it's enabled
    if (!prePassCredentialStorageEnabled) {
      return;
    }
    // this is the default 'during validation' flow
    hasNewCredentialsToStore(validationId).then((newCredentialsToStore: boolean) => {
      if (abortController.signal.aborted) {
        return;
      }
      if (
        !newCredentialsToStore ||
        (currentLocation?.state?.prevPath !== Routes.TOKEN_ACTIVE && storeStatus === StoreStatus.complete)
      ) {
        if (!currentLocation?.state?.prevPath) {
          getRouteForCurrentStatus().then((statusRoute) => {
            if (statusRoute) navigate(statusRoute, { state: { prevPath: Routes.STORE_CREDENTIALS } });
          });
        } else {
          navigate(currentLocation?.state?.prevPath, { state: { prevPath: Routes.STORE_CREDENTIALS } });
        }
      }
    });

    // eslint-disable-next-line consistent-return
    return () => {
      abortController.abort();
    };
  }, [
    currentLocation?.state?.prevPath,
    prePassCredentialStorageEnabled,
    navigate,
    storeStatus,
    flowSuccessful,
    getRouteForCurrentStatus,
    hasNewCredentialsToStore,
    validationId,
  ]);

  const isInProgress = useMemo(() => {
    return [StoreStatus.inProgress, StoreStatus.confirmed].includes(storeStatus);
  }, [storeStatus]);

  return (
    <Container>
      {retrieveExistingInProgress || !scopeRequest ? (
        <div className="mb-8">
          <Loading />
        </div>
      ) : (
        <>
          <TitleContainer center>
            <h1 className="font-normal mb-4" data-testid="STORE_CREDENTIAL_SCREEN">
              <span>🔒 </span>
              {storeStatus !== StoreStatus.complete
                ? t('screens.credentialStorage.title')
                : t('screens.credentialStorage.success.title')}
            </h1>

            <div data-testid="STORE_STATUS">
              <>
                {![StoreStatus.complete, StoreStatus.failed].includes(storeStatus) && (
                  <div className="mb-4 p pass-status-text"> {t('screens.credentialStorage.description')}</div>
                )}
              </>
              <>
                {storeStatus === StoreStatus.complete && (
                  <>
                    <div
                      data-testid="screens.credentialStorage.success.civicMe"
                      className="mt-10 text-lg leading-6 text-textParagraph pass-status-text"
                    >
                      <span>{t('screens.credentialStorage.success.civicMe')}</span>
                    </div>

                    <div className="mt-4 mb-6">
                      <a
                        data-testid="CIVIC_ME_HYPERLINK"
                        className="text-lg textLink"
                        href="www.civic.me"
                        target="_blank"
                        rel="noreferrer"
                      >
                        <div className="inline-block align-middle">
                          <span className="inline-block align-middle mr-2">
                            {t('screens.tokenActive.visitCivicMe')}
                          </span>
                          <span className="mr-2 inline-block align-middle">
                            <SVGCivicMe />
                          </span>
                          <span className="inline-block align-middle">{t('screens.tokenActive.civicMeLink')}</span>
                        </div>
                      </a>
                    </div>
                  </>
                )}
              </>
              <>
                {storeStatus === StoreStatus.failed && (
                  <div className="mb-4 p pass-status-text">{t('screens.credentialStorage.error')}</div>
                )}
              </>
            </div>
          </TitleContainer>
          <ButtonContainer bottomMargin="mb-2 mt-4">
            <Button
              style={ButtonStyle.DEFAULT}
              disabled={isInProgress}
              loading={isInProgress}
              dataTestid="CONFIRM_STORE_BUTTON"
              onClick={() => onConfirm()}
            >
              <span>{t('screens.credentialStorage.continueButton')}</span>
            </Button>
          </ButtonContainer>
          {failureCount >= 2 && (
            <ButtonContainer bottomMargin="mb-2 mt-4">
              <Button
                style={ButtonStyle.BACK}
                disabled={false}
                loading={false}
                dataTestid="SKIP_STORE_CREDENTIALS_CREDENTIALS_BUTTON"
                onClick={() => skipStorageConfirm()}
              >
                <span>{t('screens.credentialStorage.skipButton')}</span>
              </Button>
            </ButtonContainer>
          )}
        </>
      )}
    </Container>
  );
}
export default withRouteAnalytics(StoreCredentialsScreen, Screens.STORE_CREDENTIALS);
